// src/components/PillolaOre.js
// ═══════════════════════════════════════════════════════════════════════════
// LA PILLOLA DI «LE MIE ORE» (ottobre 2026, W406)
// ═══════════════════════════════════════════════════════════════════════════
// Uscendo da «Le mie ore» con il tempo acceso (o in pausa), resta in tutta Nexiora una
// pillola piccola: «⏱ AI-26-034 · 1:23:45» con ⏸/▶ e ⏹. È lei a ricordare che il conteggio
// continua.
//  • si trascina dove si vuole (la posizione resta salvata su questo dispositivo);
//  • toccandola si riapre «Le mie ore» a tutto schermo;
//  • ✕ la nasconde, il conteggio continua: ricompare alla prossima apertura di Nexiora o
//    dopo 4 ore, se il tempo è ancora acceso (per non dimenticarlo acceso a fine giornata);
//  • sul computer, con Chrome o Edge, «📌» la apre in una finestrella separata che resta
//    sopra tutti i programmi (Document Picture-in-Picture); sugli altri browser e sul
//    telefono il pulsante non c'è.
// Stessa logica di «Le mie ore» (pages/MieOrePage.js): meno di un minuto è un tocco per
// sbaglio e il blocco si toglie; la pausa si ricorda sul dispositivo.
// ═══════════════════════════════════════════════════════════════════════════
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { collection, doc, query, where, onSnapshot, writeBatch, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useToast } from "../utils/Toast";
import { cronometro, giornoDi, UFFICIO } from "../utils/diarioOre";

const QUATTRO_ORE = 4 * 3600000;
const leggi = (k, d = null) => { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : d; } catch { return d; } };
const scrivi = (k, v) => { try { if (v === null) localStorage.removeItem(k); else localStorage.setItem(k, JSON.stringify(v)); } catch { /* */ } };
const leggiS = (k) => { try { return JSON.parse(sessionStorage.getItem(k) || "null"); } catch { return null; } };
const scriviS = (k, v) => { try { if (v === null) sessionStorage.removeItem(k); else sessionStorage.setItem(k, JSON.stringify(v)); } catch { /* */ } };
const pipDisponibile = () => typeof window !== "undefined" && "documentPictureInPicture" in window && !window.matchMedia?.("(pointer: coarse)").matches;

function Contenuto({ su, tempo, inPausa, onPausa, onRiprendi, onStop, onApri, onNascondi, onPip, compatta }) {
  const b = (bg, col) => ({ border: "none", background: bg, color: col, borderRadius: 999, width: 30, height: 30, cursor: "pointer", fontSize: 13, fontWeight: 900, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Inter',sans-serif" });
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: "'Inter',sans-serif", color: "#fff", width: compatta ? "100%" : undefined }}>
      <button type="button" onClick={onApri} title="Apri «Le mie ore»" style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", textAlign: "left", padding: "0 4px", minWidth: 0, flex: 1, fontFamily: "inherit" }}>
        <span style={{ display: "block", fontSize: 10.5, fontWeight: 700, color: inPausa ? "#fcd34d" : "#c4b5fd", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 170 }}>{inPausa ? "⏸ In pausa · " : "⏱ "}{su}</span>
        <span style={{ display: "block", fontSize: 17, fontWeight: 900, fontVariantNumeric: "tabular-nums", lineHeight: 1.1 }}>{tempo}</span>
      </button>
      {inPausa
        ? <button type="button" onClick={onRiprendi} title="Riprendi" aria-label="Riprendi" style={b("#7c3aed", "#fff")}>▶</button>
        : <button type="button" onClick={onPausa} title="Pausa" aria-label="Pausa" style={b("#fff", "#1e1b4b")}>❚❚</button>}
      <button type="button" onClick={onStop} title="Stop: hai finito di lavorare" aria-label="Stop" style={b("#fecaca", "#b91c1c")}>■</button>
      {onPip && <button type="button" onClick={onPip} title="Tienila sempre in vista, sopra gli altri programmi" aria-label="Sempre in vista" style={b("rgba(255,255,255,.14)", "#fff")}>📌</button>}
      {onNascondi && <button type="button" onClick={onNascondi} title="Nascondi: il conteggio continua" aria-label="Nascondi la pillola" style={b("rgba(255,255,255,.14)", "#fff")}>✕</button>}
    </div>
  );
}

export default function PillolaOre({ onApri }) {
  const { currentUser } = useAuth();
  const { effectiveOrgId: orgId } = useOrg();
  const { addToast } = useToast();
  const uid = currentUser?.uid;
  const kPausa = `nexiora_ore_pausa_${uid}`, kPos = `nexiora_pillola_pos_${uid}`, kNascosta = `nexiora_pillola_nascosta_${uid}`;
  const [inCorso, setInCorso] = useState(null);
  const [pausa, setPausa] = useState(() => leggi(kPausa));
  const [adesso, setAdesso] = useState(Date.now());
  const [pos, setPos] = useState(() => leggi(kPos));
  const [nascosta, setNascosta] = useState(() => leggiS(kNascosta));
  const [pip, setPip] = useState(null);       // finestra «sempre in vista»
  const [occupato, setOccupato] = useState(false);
  const trascina = useRef(null);
  const elRef = useRef(null);

  useEffect(() => {
    if (!orgId || !uid) return undefined;
    return onSnapshot(query(collection(db, "diarioOre"), where("orgId", "==", orgId), where("uid", "==", uid), where("inCorso", "==", true)),
      s => setInCorso(s.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (b.inizio || 0) - (a.inizio || 0))[0] || null), () => setInCorso(null));
  }, [orgId, uid]);
  // la pausa si legge di nuovo quando si torna da «Le mie ore» (o da un'altra scheda)
  useEffect(() => {
    const rileggi = () => setPausa(leggi(kPausa));
    window.addEventListener("storage", rileggi); window.addEventListener("focus", rileggi);
    const t = setInterval(rileggi, 5000);
    return () => { window.removeEventListener("storage", rileggi); window.removeEventListener("focus", rileggi); clearInterval(t); };
  }, [kPausa]);
  const attiva = !!(inCorso || pausa);
  useEffect(() => {
    if (!attiva) return undefined;
    const t = setInterval(() => setAdesso(Date.now()), 1000);
    return () => clearInterval(t);
  }, [attiva]);
  // finestrella chiusa dall'utente: si torna alla pillola
  useEffect(() => {
    if (!pip) return undefined;
    const chiusa = () => setPip(null);
    pip.addEventListener("pagehide", chiusa);
    return () => pip.removeEventListener("pagehide", chiusa);
  }, [pip]);
  useEffect(() => { if (!attiva && pip) { try { pip.close(); } catch { /* */ } } }, [attiva, pip]);

  if (!uid || !attiva) return null;
  const vecchio = inCorso && inCorso.data && inCorso.data < giornoDi(adesso);
  if (vecchio) return null; // tempo rimasto acceso da ieri: lo si sistema in «Le mie ore» (avviso in Home)
  // pausa lasciata da un giorno precedente: non si mostra «In pausa» da giorni (W415); si
  // riprende da «Le mie ore» se serve
  if (!inCorso && pausa?.dal && giornoDi(pausa.dal) < giornoDi(adesso)) return null;
  const tempoAcceso = inCorso ? adesso - inCorso.inizio : 0;
  const nascostaOra = nascosta && !(inCorso && adesso - (nascosta.dal || 0) > QUATTRO_ORE);
  const blocco = inCorso || pausa;
  const su = blocco?.projectId ? (blocco.codiceCommessa || blocco.projectNome || "Commessa") : UFFICIO.nome;
  const tempo = inCorso ? cronometro(tempoAcceso) : cronometro(adesso - (pausa?.dal || adesso));

  const chiudi = (b, blocco, ora) => {
    if (ora - (blocco.inizio || ora) < 60000) b.delete(doc(db, "diarioOre", blocco.id));
    else b.update(doc(db, "diarioOre", blocco.id), { fine: ora, inCorso: false, updatedAt: serverTimestamp() });
  };
  const ferma = async (conPausa) => {
    if (occupato) return;
    if (!inCorso) { if (!conPausa) { scrivi(kPausa, null); setPausa(null); } return; }
    setOccupato(true);
    try {
      const b = writeBatch(db); chiudi(b, inCorso, Date.now()); await b.commit();
      const p = conPausa ? { projectId: inCorso.projectId || "", projectNome: inCorso.projectNome || "", codiceCommessa: inCorso.codiceCommessa || "", dal: Date.now() } : null;
      scrivi(kPausa, p); setPausa(p);
      if (!conPausa) addToast("Tempo fermato ✅", "success");
    } catch { addToast("Non sono riuscito a fermare il tempo: riprova", "error"); }
    finally { setOccupato(false); }
  };
  const riprendi = async () => {
    if (occupato || !pausa) return;
    setOccupato(true);
    try {
      const ora = Date.now();
      const b = writeBatch(db);
      b.set(doc(collection(db, "diarioOre")), { orgId, uid, nomeUtente: currentUser?.displayName || currentUser?.email || "", data: giornoDi(ora),
        projectId: pausa.projectId || "", projectNome: pausa.projectNome || "", codiceCommessa: pausa.codiceCommessa || "",
        inizio: ora, fine: null, minuti: null, inCorso: true, nota: "", origine: "cronometro", createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
      await b.commit();
      scrivi(kPausa, null); setPausa(null);
    } catch { addToast("Non sono riuscito a far ripartire il tempo: riprova", "error"); }
    finally { setOccupato(false); }
  };
  const nascondi = () => { const v = { dal: Date.now() }; scriviS(kNascosta, v); setNascosta(v); addToast("Pillola nascosta: il conteggio continua", "info"); };
  const apriPip = async () => {
    try {
      const w = await window.documentPictureInPicture.requestWindow({ width: 300, height: 76 });
      w.document.body.style.cssText = "margin:0;background:#1e1b4b;display:flex;align-items:center;padding:0 10px;overflow:hidden;";
      w.document.title = "Le mie ore";
      setPip(w);
    } catch { addToast("Il browser non ha aperto la finestra: usa la pillola", "info"); }
  };

  const props = { su, tempo, inPausa: !inCorso, onPausa: () => ferma(true), onRiprendi: riprendi, onStop: () => ferma(false),
    onApri: () => { if (pip) { try { window.focus(); } catch { /* */ } } onApri(); } };
  if (pip) return createPortal(<Contenuto {...props} compatta />, pip.document.body);
  if (nascostaOra) return null;

  // trascinamento (mouse e dito): la pillola resta dove la si lascia
  const inizia = (e) => {
    if (e.target.closest("button")) return;
    const r = elRef.current.getBoundingClientRect();
    trascina.current = { dx: e.clientX - r.left, dy: e.clientY - r.top };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const muovi = (e) => {
    if (!trascina.current) return;
    const w = elRef.current.offsetWidth, h = elRef.current.offsetHeight;
    setPos({ x: Math.max(4, Math.min(window.innerWidth - w - 4, e.clientX - trascina.current.dx)), y: Math.max(4, Math.min(window.innerHeight - h - 4, e.clientY - trascina.current.dy)) });
  };
  const fine = () => { if (trascina.current) { trascina.current = null; scrivi(kPos, pos); } };
  const stile = pos
    ? { left: Math.min(pos.x, window.innerWidth - 80), top: Math.min(pos.y, window.innerHeight - 50) }
    : window.innerWidth < 768 ? { left: 12, bottom: 84 } : { left: "50%", bottom: 20, transform: "translateX(-50%)" }; // sul computer: in basso al centro, lontano dal menu

  return (
    <div ref={elRef} className="nx-pillola-ore" onPointerDown={inizia} onPointerMove={muovi} onPointerUp={fine} onPointerCancel={fine}
      style={{ position: "fixed", zIndex: 940, ...stile, background: "#1e1b4b", borderRadius: 999, padding: "6px 8px 6px 10px", boxShadow: "0 8px 24px rgba(30,27,75,.35)",
        touchAction: "none", cursor: "grab", userSelect: "none", maxWidth: "calc(100vw - 24px)" }}>
      <Contenuto {...props} onNascondi={nascondi} onPip={pipDisponibile() ? apriPip : null} />
    </div>
  );
}
